import { MapPin, Clock, MessageSquare, Instagram, ArrowUpRight } from "lucide-react";
import { BRAND } from "../mock";
import { buildBookingSmsHref } from "../lib/bookingSms";

// Direct profile link. most reliable cross-platform.
// On mobile this deep-links into the Instagram app; on desktop it opens
// the IG web profile where the visitor can hit "Message".
const INSTAGRAM_PROFILE_URL = "https://www.instagram.com/jesterwerks.gta/";

export default function Contact() {
  return (
    <section
      id="contact"
      className="relative py-28 md:py-36 overflow-hidden"
      data-testid="contact-section"
    >
      <div className="absolute -bottom-40 -right-40 w-[700px] h-[700px] rounded-full bg-jester-purple/10 blur-[140px]" />
      <div className="absolute -top-20 left-1/4 w-[500px] h-[500px] rounded-full bg-acid/6 blur-[140px]" />

      <div className="relative max-w-6xl mx-auto px-6 md:px-10">
        {/* Heading */}
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-[0.3em] text-acid mb-5 font-label">
           Get In Touch
          </div>
          <h2 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.95]">
            Let&rsquo;s lock<br />
            <span className="text-jester">your car in.</span>
          </h2>
          <p className="mt-7 text-white/75 text-lg leading-relaxed max-w-2xl">
            Skip the forms. Text us your vehicle details or DM us on
            Instagram. you&rsquo;ll get a real human reply, usually
            within an hour.
          </p>
        </div>

        {/* 2 big action cards */}
        <div className="mt-14 grid md:grid-cols-2 gap-5 max-w-4xl">
          <ActionCard
            href={buildBookingSmsHref()}
            icon={MessageSquare}
            label="Text Us"
            value={BRAND.phone}
            sub="Fastest reply &middot; pre-filled template"
            accent="acid"
            testId="contact-text-cta"
          />
          <ActionCard
            href={INSTAGRAM_PROFILE_URL}
            external
            icon={Instagram}
            label="DM on Instagram"
            value="@jesterwerks.gta"
            sub="Tap &lsquo;Message&rsquo; on our profile &middot; we reply 24/7"
            accent="jester"
            testId="contact-instagram-cta"
          />
        </div>

        {/* Studio info row */}
        <div className="mt-14 grid sm:grid-cols-2 gap-8 pt-10 border-t border-white/8">
          <div className="flex items-start gap-5">
            <div className="w-12 h-12 rounded-full border border-white/10 bg-white/5 flex items-center justify-center flex-shrink-0">
              <MapPin className="w-5 h-5" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-white/45 mb-2 font-label">
                Two Studios
              </div>
              {BRAND.locations.map((loc) => (
                <div key={loc.id} className="mb-2 last:mb-0">
                  <div className="text-acid text-[10px] uppercase tracking-[0.2em] font-label">
                    {loc.name}
                  </div>
                  <div className="text-white text-sm">{loc.full}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-start gap-5">
            <div className="w-12 h-12 rounded-full border border-white/10 bg-white/5 flex items-center justify-center flex-shrink-0">
              <Clock className="w-5 h-5" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-white/45 mb-2 font-label">
                Studio Hours
              </div>
              {BRAND.hours.map((h, i) => (
                <div
                  key={i}
                  className="flex items-center gap-6 text-sm text-white/85"
                >
                  <span className="w-32 text-white/55">{h.day}</span>
                  <span>{h.time}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ActionCard({
  href,
  external,
  icon: Icon,
  label,
  value,
  sub,
  accent,
  testId,
}) {
  const accentStyles = {
    acid: {
      ring: "hover:border-acid hover:shadow-[0_0_30px_rgba(168,224,0,0.25)]",
      iconBg: "bg-acid text-[#120127]",
      labelColor: "text-acid",
    },
    jester: {
      ring: "hover:border-jester hover:shadow-[0_0_30px_rgba(108,38,217,0.35)]",
      iconBg: "bg-jester-purple text-white",
      labelColor: "text-jester",
    },
    white: {
      ring: "hover:border-white/40",
      iconBg: "bg-white text-[#120127]",
      labelColor: "text-white",
    },
  }[accent];

  return (
    <a
      href={href}
      data-testid={testId}
      {...(external
        ? { target: "_blank", rel: "noopener noreferrer" }
        : {})}
      className={`group relative flex flex-col gap-5 p-7 rounded-2xl border border-white/10 bg-[#120127]/70 backdrop-blur-sm transition-all duration-300 hover:bg-[#120127]/90 ${accentStyles.ring}`}
    >
      <div className="flex items-center justify-between">
        <div
          className={`w-12 h-12 rounded-full ${accentStyles.iconBg} flex items-center justify-center`}
        >
          <Icon className="w-5 h-5" />
        </div>
        <ArrowUpRight className="w-5 h-5 text-white/30 group-hover:text-white group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
      </div>
      <div>
        <div
          className={`text-[10px] uppercase tracking-[0.3em] font-label mb-2 ${accentStyles.labelColor}`}
        >
          {label}
        </div>
        <div className="font-display text-2xl text-white leading-tight break-words">
          {value}
        </div>
        <div className="mt-3 text-sm text-white/55 leading-relaxed">{sub}</div>
      </div>
    </a>
  );
}
